import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeftSmall, ChevronRightSmall } from 'ui/icons';
import { IconButton, BUTTON_VARIANTS, BUTTON_SIZES } from 'ui/button';
import { useFlowChart } from 'ui/workflows/flowchart';
import { getClonedFromJourneyInvalidNodes, getNodes, selectNodes } from 'modules/workflows/ducks';
import {
  NavigationContainer,
  NavigationLabel,
  ReviewNeededContainer,
  ReviewNeededMsg,
  ReviewNeededMsgBody,
  ReviewNeededMsgHeader,
} from './ReviewNeeded.styled';

export const REVIEW_NEEDED_TEST_ID = 'review-needed';
export const REVIEW_COUNT = 'review-count';
export const REVIEW_PREV = 'review-prev';
export const REVIEW_NEXT = 'review-next';

export const ReviewNeeded = () => {
  const dispatch = useDispatch();
  const { focus } = useFlowChart();

  const invalidClonedNodes = useSelector(getClonedFromJourneyInvalidNodes);
  const nodes = useSelector(getNodes);

  const numOfReviews = invalidClonedNodes?.length;
  const numTiles = `${numOfReviews} tile${numOfReviews > 1 ? 's' : ''}`;

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  useEffect(() => {
    const selectedNodes = nodes.filter((node) => node.isSelected);
    const selectedId = selectedNodes.length === 1 ? selectedNodes[0].id : undefined;
    const selectedIdx = invalidClonedNodes.indexOf(selectedId as string);

    // if selecting a node that is invalid, this will update counter
    // otherwise this will set the counter to its default state
    setSelectedIndex(selectedIdx);
  }, [nodes, invalidClonedNodes]);

  const selectNode = (idx: number) => {
    let nextIdx = idx;
    if (idx < 0) nextIdx = numOfReviews - 1;
    if (idx === numOfReviews) nextIdx = 0;

    const selected = invalidClonedNodes[nextIdx];

    dispatch(selectNodes([selected]));
    focus(selected, true);
  };

  return (
    <ReviewNeededContainer data-test={REVIEW_NEEDED_TEST_ID}>
      <ReviewNeededMsg>
        <ReviewNeededMsgHeader>Review needed</ReviewNeededMsgHeader>
        <ReviewNeededMsgBody>
          Some resources were not copied across projects.
          <br />
          Please review the affected tiles and update for the current project.
        </ReviewNeededMsgBody>
      </ReviewNeededMsg>
      <NavigationContainer>
        <IconButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
          onClick={() => selectNode(selectedIndex - 1)}
          data-test={REVIEW_PREV}
        >
          <ChevronLeftSmall />
        </IconButton>
        <NavigationLabel data-test={REVIEW_COUNT}>
          {selectedIndex >= 0 ? `${selectedIndex + 1} of ${numTiles}` : `${numTiles} affected`}
        </NavigationLabel>
        <IconButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
          onClick={() => selectNode(selectedIndex + 1)}
          data-test={REVIEW_NEXT}
        >
          <ChevronRightSmall />
        </IconButton>
      </NavigationContainer>
    </ReviewNeededContainer>
  );
};
